import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, Compass, Lock, ScrollText, X } from "lucide-react";
import { useWorld } from "../state/World";
import { api } from "../lib/api";
import { PRACTICES } from "../lib/practices";
import { PLACES as WORLD_PLACES } from "../lib/world";

const TABS = ["Practices", "Journey", "The World"];

const PLACES = WORLD_PLACES.map((p) => ({
  name: p.place,
  sub: p.lit ? p.space : p.sub,
  to: p.to,
  realm: p.key,
  lit: p.lit,
}));

/**
 * The Guidebook. The navigation spine of the World.
 *
 * Verb-led by design: a Practice is what the human needs to DO. Which Place it
 * happens in is routing detail they should not have to carry, so it is shown as
 * a whisper, never as the instruction.
 */
export const Guidebook = ({ bright }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("Practices");
  const [entries, setEntries] = useState(null);
  const { cross } = useWorld();
  const nav = useNavigate();

  useEffect(() => {
    if (open && tab === "Journey" && entries === null) {
      api.get("/journal").then(({ data }) => setEntries(data)).catch(() => setEntries([]));
    }
  }, [open, tab, entries]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const go = async (realm, to) => {
    setOpen(false);
    const { external } = await cross(realm, to);
    if (external) return;
    nav(to);
  };

  return (
    <>
      <button
        type="button"
        data-testid="guidebook-toggle"
        aria-label="Open the Guidebook"
        title="The Guidebook"
        onClick={() => setOpen((v) => !v)}
        className="glass flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105"
      >
        <BookOpen size={16} strokeWidth={1.6} className="text-[var(--stage-gold)]" />
        <span className="mono text-[9px] uppercase tracking-[0.18em] text-white/75">
          Guide
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* click-away only. Deliberately invisible: no scrim, no blur — the
                World is never degraded to make a panel readable. */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="guidebook-panel"
              className="glass fixed bottom-4 right-4 z-50 flex max-h-[76vh] w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-3xl"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <div>
                  <div className="tracked text-[9px] text-[var(--stage-gold)]">The Guidebook</div>
                  <div className="serif mt-0.5 text-lg leading-none text-white/90">
                    What do you need to do?
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="guidebook-close"
                  aria-label="Close the Guidebook"
                  onClick={() => setOpen(false)}
                  className="text-white/40 transition-colors hover:text-white/80"
                >
                  <X size={16} strokeWidth={1.6} />
                </button>
              </header>

              <nav className="flex gap-1 border-b border-white/10 px-4 pt-3">
                {TABS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`guidebook-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setTab(t)}
                    className={`tracked relative px-3 pb-3 text-[9px] transition-colors ${
                      tab === t ? "text-[var(--stage-gold)]" : "text-white/35 hover:text-white/65"
                    }`}
                  >
                    {t}
                    {tab === t && (
                      <motion.span
                        layoutId="guidebook-tab"
                        className="absolute inset-x-2 -bottom-px h-px bg-[var(--stage-gold)]"
                      />
                    )}
                  </button>
                ))}
              </nav>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {tab === "Practices" && (
                  <ul className="space-y-1" data-testid="guidebook-practices">
                    {PRACTICES.map((p) => (
                      <li key={p.key}>
                        <button
                          type="button"
                          data-testid={`practice-${p.key}`}
                          onClick={() => go(p.realm, p.to)}
                          className="group flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition-colors duration-200 hover:bg-white/[0.06]"
                        >
                          <ChevronRight
                            size={14}
                            strokeWidth={1.8}
                            className="mt-0.5 shrink-0 text-white/25 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-[var(--stage-gold)]"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm leading-snug text-white/85 group-hover:text-white">
                              {p.verb}
                            </span>
                            <span className="mono mt-1 block text-[10px] leading-relaxed text-white/35">
                              {p.note}
                            </span>
                            {p.where && (
                              <span className="mono mt-1 block text-[9px] text-white/20">
                                {p.where}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {tab === "Journey" && (
                  <div data-testid="guidebook-journey">
                    {entries === null && (
                      <div className="mono px-3 py-6 text-[10px] text-white/25">Turning the pages…</div>
                    )}
                    {entries?.length === 0 && (
                      <div className="mono px-3 py-6 text-[10px] text-white/25">
                        Nothing written yet. Pick a Practice.
                      </div>
                    )}
                    <ol className="space-y-0">
                      {entries?.slice(0, 24).map((e) => (
                        <li
                          key={e.id}
                          data-testid="guidebook-journey-entry"
                          className="relative border-l border-white/10 pb-5 pl-6 last:pb-1"
                        >
                          <span className="absolute -left-[3px] top-1.5 h-1.5 w-1.5 rounded-full bg-[var(--stage-gold)]/70" />
                          <div className="mono mb-1 flex items-center gap-2 text-[9px] text-white/25">
                            <span className="tracked text-[var(--stage-gold)]/70">{e.realm}</span>
                            <span>{e.created_at?.slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <div className="text-[12px] leading-relaxed text-white/70">{e.text}</div>
                        </li>
                      ))}
                    </ol>
                    <button
                      type="button"
                      data-testid="hud-journal-link"
                      onClick={() => go("endeavorum", "/journal")}
                      className="tracked mt-2 w-full rounded-2xl border border-white/10 px-3 py-3 text-[9px] text-white/45 transition-colors hover:border-[var(--stage-gold)]/40 hover:text-white/80"
                    >
                      Open the full Journal
                    </button>
                  </div>
                )}

                {tab === "The World" && (
                  <ul className="space-y-1" data-testid="guidebook-world">
                    <li>
                      <button
                        type="button"
                        data-testid="hud-atlas-link"
                        onClick={() => go("endeavorum", "/")}
                        className="group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-white/[0.06]"
                      >
                        <Compass size={14} strokeWidth={1.6} className="text-white/40 group-hover:text-[var(--atlas-cyan)]" />
                        <span className="text-sm text-white/85">The Atlas</span>
                        <span className="mono ml-auto text-[9px] text-white/25">the whole World</span>
                      </button>
                    </li>
                    {PLACES.map((p) => (
                      <li key={p.name}>
                        <button
                          type="button"
                          disabled={!p.lit}
                          data-testid={`guidebook-place-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={p.lit ? () => go(p.realm, p.to) : undefined}
                          className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors ${
                            p.lit ? "hover:bg-white/[0.06]" : "cursor-default opacity-40"
                          }`}
                        >
                          {p.lit ? (
                            <ChevronRight size={14} strokeWidth={1.8} className="text-white/25 group-hover:text-[var(--stage-gold)]" />
                          ) : (
                            <Lock size={12} strokeWidth={1.6} className="text-white/30" />
                          )}
                          <span className="text-sm text-white/85">{p.name}</span>
                          <span className="mono ml-auto text-[9px] text-white/25">
                            {p.lit ? p.sub : "not open yet"}
                          </span>
                        </button>
                      </li>
                    ))}
                    <li className="mt-2 border-t border-white/10 pt-2">
                      <button
                        type="button"
                        data-testid="guidebook-ledger"
                        onClick={() => go("endeavorum", "/ledger")}
                        className="group flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-white/[0.06]"
                      >
                        <ScrollText size={14} strokeWidth={1.6} className="text-white/40 group-hover:text-[var(--stage-gold)]" />
                        <span className="text-sm text-white/85">The Ledger</span>
                        <span className="mono ml-auto text-[9px] text-white/25">every fee, honestly</span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Guidebook;
